'use client'

import React, { useRef, useState, useCallback, RefObject, useEffect } from 'react'

import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'

const minSwipeDistance = 50
const sliderSpeed = 0.5

type UseSliderProps<T> = {
  childData: Array<T>
}

type UseSliderReturnValue = {
  currentCardIndex: number
  itemRefs: Array<RefObject<HTMLDivElement | null>>
  handleOnTouchStart: React.TouchEventHandler<HTMLDivElement>
  handleOnTouchMove: React.TouchEventHandler<HTMLDivElement>
  handleOnTouchEnd: React.TouchEventHandler<HTMLDivElement>
  goToNextItem: () => void
  goToPreviousItem: () => void
  wrapperDivRef: RefObject<HTMLDivElement | null>
  showButtons: boolean
}

const useSlider = <T,>({ childData }: UseSliderProps<T>): UseSliderReturnValue => {
  const [currentCardIndex, setCurrentCardIndex] = useState(0)
  const wrapperDivRef = useRef<HTMLDivElement>(null)
  const touchStart = useRef<number | null>(null)
  const touchEnd = useRef<number | null>(null)
  const itemRefs = useRef<Array<RefObject<HTMLDivElement | null>>>(
    childData.map(() => React.createRef()),
  )
  const [showButtons, setShowButtons] = useState(true)
  const breakpoints = useBreakpoint()

  function isFullyVisible(element) {
    const rect = element.getBoundingClientRect()

    return (
      rect.left >= 0 && rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    )
  }

  useEffect(() => {
    const checkButtonVisibility = () => {
      const wrapperRef = wrapperDivRef.current
      if (wrapperRef) {
        setShowButtons(!isFullyVisible(wrapperRef))
      }
    }

    checkButtonVisibility()
  }, [])

  useEffect(() => {
    const resetSlider = () => {
      const wrapperRef = wrapperDivRef.current
      const itemRef = itemRefs.current[0]?.current
      if (wrapperRef && itemRef) {
        setShowButtons(!isFullyVisible(wrapperRef))
        setCurrentCardIndex(0)
        wrapperRef.style.transform = `translateX(${-itemRef.offsetWidth * 0}px)`
        wrapperRef.style.transition = `all ${sliderSpeed}s`
      }
    }

    window.addEventListener('resize', resetSlider)
  }, [])

  const validateNextIndex = useCallback(
    (index: number) => (index === childData.length - 1 ? 0 : index + 1),
    [childData.length],
  )

  const validatePreviousIndex = useCallback(
    (index: number) => (index === 0 ? childData.length - 1 : index - 1),
    [childData.length],
  )

  const goToNextItem = useCallback(() => {
    const nextIndex = validateNextIndex(currentCardIndex)
    const sliderItem = itemRefs.current[nextIndex]?.current
    const lastItemRef = itemRefs.current[childData.length - 1]?.current
    const wrapper = wrapperDivRef.current
    const isLastItemVisible = isFullyVisible(lastItemRef)

    if (sliderItem && wrapper && isLastItemVisible) {
      wrapper.style.transform = `translateX(${-sliderItem.offsetWidth * 0}px)`
      wrapper.style.transition = `all ${sliderSpeed}s`
      setCurrentCardIndex(0)
    }

    if (sliderItem && wrapper && !isLastItemVisible) {
      if (breakpoints.wide && showButtons) {
        wrapper.style.transform = `translateX(${-sliderItem.offsetWidth * nextIndex}px)`
        wrapper.style.transition = `all ${sliderSpeed}s`
        setCurrentCardIndex(validateNextIndex)
      } else {
        const isWrapperTooWide = wrapper.scrollWidth > document.documentElement.clientWidth

        if (!isFullyVisible(sliderItem) || isWrapperTooWide || !isFullyVisible(wrapper)) {
          wrapper.style.transform = `translateX(${-sliderItem.offsetWidth * nextIndex}px)`
          wrapper.style.transition = `all ${sliderSpeed}s`
          setCurrentCardIndex(validateNextIndex)
        }
      }
    }
  }, [validateNextIndex, currentCardIndex, childData.length, breakpoints.wide, showButtons])

  const goToPreviousItem = useCallback(() => {
    const prevIndex = validatePreviousIndex(currentCardIndex)
    const sliderItem = itemRefs.current[prevIndex]?.current
    const wrapper = wrapperDivRef.current

    if (sliderItem && wrapper && currentCardIndex !== 0) {
      if (breakpoints.wide && showButtons) {
        wrapper.style.transform = `translateX(${-sliderItem.offsetWidth * prevIndex}px)`
        wrapper.style.transition = `all ${sliderSpeed}s`
        setCurrentCardIndex(prevIndex)
      } else {
        const isWrapperTooWide = wrapper.scrollWidth > document.documentElement.clientWidth

        if (!isFullyVisible(sliderItem) || isWrapperTooWide || !isFullyVisible(wrapper)) {
          wrapper.style.transform = `translateX(${-sliderItem.offsetWidth * prevIndex}px)`
          wrapper.style.transition = `all ${sliderSpeed}s`
          setCurrentCardIndex(prevIndex)
        }
      }
    }
  }, [currentCardIndex, validatePreviousIndex, showButtons, breakpoints])

  const handleOnTouchStart: React.TouchEventHandler<HTMLDivElement> = useCallback(element => {
    touchEnd.current = null
    touchStart.current = element.targetTouches[0]!.clientX
  }, [])

  const handleOnTouchMove: React.TouchEventHandler<HTMLDivElement> = useCallback(element => {
    touchEnd.current = element.targetTouches[0]!.clientX
  }, [])

  const handleOnTouchEnd: React.TouchEventHandler<HTMLDivElement> = useCallback(() => {
    if (!touchStart.current || !touchEnd.current) return
    const distance = touchStart.current - touchEnd.current
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe) {
      goToNextItem()
    }

    if (isRightSwipe) {
      goToPreviousItem()
    }
  }, [goToNextItem, goToPreviousItem])

  return {
    currentCardIndex,
    itemRefs: itemRefs.current,
    handleOnTouchStart,
    handleOnTouchMove,
    handleOnTouchEnd,
    goToNextItem,
    goToPreviousItem,
    wrapperDivRef,
    showButtons,
  }
}

export default useSlider
